import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Avatar,
  Container,
  Paper,
  Chip,
  Alert
} from "@mui/material";
import {
  QrCode2 as QRCodeIcon,
  Timer as TimerIcon,
  TrendingUp as ReportIcon,
  Approval as ApprovalIcon,
  Dashboard as DashboardIcon,
  Assignment as TaskIcon,
  CheckCircle as CheckCircleIcon,
  SupervisorAccount as SupervisorIcon
} from "@mui/icons-material";
import { useUserContext } from "../context/UserContext";
import CustomAppBar from "../components/CustomAppBar";

const SupervisorDashboard = () => {
  const navigate = useNavigate();
  const { selectedTenantName } = useUserContext();

  const supervisorActions = [
    {
      title: "Time Booking Approval",
      description: "Approve or reject time bookings from supervised users",
      icon: <CheckCircleIcon sx={{ fontSize: 40, color: "#2e7d32" }} />,
      action: () => navigate('/time-booking-approval'),
      color: "#e8f5e8",
      primary: true
    },
    {
      title: "Scan QR Code",
      description: "Scan QR code to join an event and start time booking",
      icon: <QRCodeIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      action: () => navigate('/scan'),
      color: "#e3f2fd"
    },
    {
      title: "Time Booking",
      description: "Start/stop your time tracking",
      icon: <TimerIcon sx={{ fontSize: 40, color: "#388e3c" }} />,
      action: () => navigate('/timer'),
      color: "#e8f5e8"
    },
    {
      title: "View Attendance",
      description: "Check your attendance and time records",
      icon: <ReportIcon sx={{ fontSize: 40, color: "#f57c00" }} />,
      action: () => navigate('/report'),
      color: "#fff3e0"
    },
    {
      title: "Apply Leave",
      description: "Submit leave requests to your admin",
      icon: <ApprovalIcon sx={{ fontSize: 40, color: "#7b1fa2" }} />,
      action: () => navigate('/applyleave'),
      color: "#f3e5f5"
    },
    {
      title: "Assigned Tasks",
      description: "View tasks assigned by your admin",
      icon: <TaskIcon sx={{ fontSize: 40, color: "#d32f2f" }} />,
      action: () => navigate('/assigntimer'),
      color: "#ffebee"
    }
  ];

  const hasActiveEvent = !!sessionStorage.getItem('activeQREvent');

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      py: 4
    }}>
      <CustomAppBar />
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: "#2e7d32", mr: 2 }}>
              <SupervisorIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Supervisor Dashboard
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Welcome back! Manage approvals and your time tracking
              </Typography>
              {selectedTenantName && (
                <Chip 
                  label={`Tenant: ${selectedTenantName}`} 
                  color="primary" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {hasActiveEvent && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
            <Typography variant="body1">
              <strong>Active Event Detected!</strong> You have scanned a QR code for an active event. 
              You can now proceed to time booking.
            </Typography>
          </Alert>
        )}

        <Typography variant="h5" gutterBottom sx={{ mb: 3, color: "white", fontWeight: "bold" }}>
          Supervisor Functions
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {supervisorActions.map((action, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: "100%",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6
                  },
                  borderRadius: 3,
                  border: action.primary ? "2px solid #2e7d32" : "none"
                }}
                onClick={action.action}
              >
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Box sx={{ mb: 2, p: 2, backgroundColor: action.color, borderRadius: 2 }}>
                    {action.icon}
                  </Box>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                  {action.primary && (
                    <Chip 
                      label="Supervisor Feature" 
                      color="success" 
                      size="small" 
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default SupervisorDashboard;


